import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import Avatar from '../dashboard/Avatar';
import GoogleGIcon from './GoogleGIcon';
import './AuthWidget.css';

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SignOutIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const ReportIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="6" y1="20" x2="6" y2="12" />
    <line x1="12" y1="20" x2="12" y2="8" />
    <line x1="18" y1="20" x2="18" y2="4" />
  </svg>
);

// Sign-in status lives in the main app header, visible on every page — not
// just the dashboard. Signed out (or a lapsed session): a "Sign in" pill
// that opens the popup. Signed in: the account pill (avatar + email, opens
// a menu with just "Sign out") plus a separate "Report" link to /dashboard —
// visible to any signed-in account; the dashboard page itself decides
// admin-vs-self content by role. Rendered on 'loading' as an empty slot
// rather than flashing the sign-in button first. The dashboard's own
// "Back to All Messages" link lives on the far left of AppHeader
// (AppHeader.tsx's backTo prop), not here — this widget only ever adds
// content on the right.
export default function AuthWidget() {
  const { status, user, openLoginModal, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const onDashboard = location.pathname === '/dashboard';

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  if (status === 'loading') {
    return <div className="auth-widget-slot" aria-hidden="true" />;
  }

  if (status === 'authenticated' && user) {
    return (
      <div className="auth-widget-slot">
        <div className="auth-widget-account" ref={containerRef}>
          <button
            className="auth-widget-account-trigger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar email={user.email} />
            <span className="auth-widget-email">{user.email}</span>
            <span className="auth-widget-chevron"><ChevronDownIcon /></span>
          </button>

          {menuOpen && (
            <div className="auth-widget-menu" role="menu">
              <div className="auth-widget-menu-email">{user.email}</div>
              <div className="auth-widget-menu-divider" />
              <button
                className="auth-widget-menu-signout"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  signOut();
                }}
              >
                <SignOutIcon />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>

        {!onDashboard && (
          <Link to="/dashboard" className="auth-widget-report-button">
            <ReportIcon />
            <span>Report</span>
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="auth-widget-slot">
      <button className="auth-widget-signin-button" onClick={openLoginModal}>
        <GoogleGIcon />
        <span>Sign in</span>
      </button>
    </div>
  );
}
